import * as THREE from 'three';
import { roomBody } from '../state/roomBody';

// Where sitting down actually puts you.
//
// Everything here is READ OFF THE MODEL rather than authored a second time. The
// generator placed the seat, so it knows its height, which way the sitter faces,
// and whether the chair reclines — and it wrote all three into the mesh as an
// affordance at construction. A room can add a chair without anyone touching
// this file.

// EYE HEIGHTS, and the reason there are two.
//
// `seatHeight` is where the SEAT SURFACE is, not where your eye ends up — a
// distinction that is obvious stated and invisible in code, where both are
// "a number near the chair". Standing, the world's eye is 0.75u (~1.65m) off the
// FLOOR. Seated, your eye is roughly 0.75m above the SEAT, which is a different
// measurement from a different origin.
export const SEATED_EYE = 0.34;    // ~0.75m above the seat surface
// STANDING, for the other direction. A PLACED seat is authored at floor level by
// standing where the cushion is — and the only thing that knows where the floor
// is at that moment is the camera, which is exactly one standing eye above it.
// The same 0.75 as PlanetScene's EYE and the one FloorClamp turns a camera
// height back into a foot height with; kept here so the placement tool does not
// have to import the scene to drop a chair. (?eye= tunes the scene's copy live —
// an admin who does that nudges the height by hand, which the panel offers.)
export const STANDING_EYE = 0.75;
// Reclined puts your head lower and further back, and tips your view up at the
// ceiling — which for a backwash is the whole experience.
export const RECLINED_EYE = 0.24;
export const RECLINED_BACK = 0.24; // world units, along −facing
export const RECLINED_PITCH = 0.5; // radians, looking up

// Find the seat inside a unit and work out the pose sitting in it produces.
// Returns null when the object has no seat, which is a legitimate answer — a
// retail case is a unit too.
export function seatPose(unit) {
  if (!unit) return null;
  let seat = null;
  unit.traverse((o) => { if (!seat && o.userData?.affordance === 'seat') seat = o; });
  if (!seat) return null;

  seat.updateWorldMatrix(true, false);
  const box = new THREE.Box3().setFromObject(seat);
  const centre = box.getCenter(new THREE.Vector3());

  // WHICH WAY YOU END UP FACING. `facing` is authored in the ROOM's space, so it
  // has to be carried through the model's own transform — a rotated or re-sited
  // room must not seat people facing a wall.
  const facing = typeof seat.userData.facing === 'number' ? seat.userData.facing : 0;
  const dir = new THREE.Vector3(Math.sin(facing), 0, Math.cos(facing))
    .transformDirection(seat.matrixWorld)
    .setY(0)
    .normalize();
  const yaw = Math.atan2(dir.x, dir.z);

  const reclined = !!seat.userData.reclined;
  const pos = new THREE.Vector3(centre.x, 0, centre.z);
  if (reclined) pos.addScaledVector(dir, -RECLINED_BACK);

  return {
    // The seat's REAL top in world space, not the authored number — the model
    // has already been fitted, grounded and possibly nudged by the time anyone
    // sits in it, and the surface you land on is the one on screen.
    pos: [pos.x, box.max.y + (reclined ? RECLINED_EYE : SEATED_EYE), pos.z],
    top: box.max.y,   // the seat surface itself (a seated body's hips go just above it: CafeGuests)
    yaw,
    pitch: reclined ? RECLINED_PITCH : 0,
    reclined,
    label: reclined ? 'Lie back' : 'Sit down',
  };
}

// ── ONE YAW CONVENTION, WRITTEN DOWN ─────────────────────────────────────────
// A pose's `yaw` is THE DIRECTION THE PLAYER FACES, world convention: 0 is +z,
// −π/2 is −x. It is deliberately NOT the camera's rotation.y, and the difference
// is exactly π, because a three.js camera looks down its own −z while everything
// else in the scene points +z.
//
// That π is worth paying attention to rather than absorbing somewhere: the two
// halves of a pose were coming from different places — the seat's from the model
// (mesh convention) and the player's from the camera (camera convention) — so
// they disagreed by half a turn and sitting down span you round to face a wall.
//
// The player faces a direction. A camera is one way of showing that, a body will
// be another, and both convert on the way out. Storing the camera's number would
// have made the body the thing that needs the correction later.
export const CAMERA_YAW_OFFSET = Math.PI;

// Where the player is right now, so standing up returns them there.
export function poseFromCamera(camera) {
  const e = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
  return {
    pos: [camera.position.x, camera.position.y, camera.position.z],
    yaw: e.y + CAMERA_YAW_OFFSET,   // camera → facing
    pitch: e.x,
  };
}

/**
 * WHERE STANDING UP PUTS YOU BACK (a seat's `from`): where you were when you sat.
 * In third person that is your BODY (state/roomBody), not the camera — the
 * camera is the chase camera a body-length behind you, and standing up there put
 * your body where the camera had been and the camera another length behind
 * THAT, inside whatever was at your back (2026-11-01, Khari, a Hype-U
 * classroom: "When I am ejected from my classroom seat, I am placed back near
 * the elevator, and the camera is tearing through it"). The facing and tilt are
 * still the camera's — the way you were looking.
 */
export function returnPose(camera) {
  const p = poseFromCamera(camera);
  if (roomBody.on) p.pos = [roomBody.v.x, roomBody.v.y, roomBody.v.z];
  return p;
}

// ── EVERY SEAT IN A ROOM, IN AN ORDER EVERY CLIENT AGREES ON ─────────────────
//
// This is what a listening room's CAPACITY is (project_world_listening_rooms).
// Khari, 2026-08-04: "limit the number so it is an intimate event... what the
// room can hold is better and will give more options." The room already knows —
// the builder that placed the furniture wrote a `seat` affordance on each piece,
// so a room that gains a couch gains capacity with nothing to author.
//
// THE SORT IS LOAD-BEARING, not tidiness. The server hands out seat INDICES
// (it cannot see furniture, and letting clients pick a chair is how two people
// share one), so every client has to turn index 3 into the same chair. Traversal
// order is stable for a given model but not something to rely on across a
// re-export, so the order is pinned to the authored NAME — which the builder
// controls and which survives a rebuild.
export function listSeats(root) {
  if (!root) return [];
  const found = [];
  root.traverse((o) => { if (o.userData?.affordance === 'seat') found.push(o); });
  found.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  // `seatPose` traverses from whatever it is given and three visits the object
  // itself first, so handing it the seat mesh resolves that same seat — no need
  // to walk back up to the unit.
  return found
    .map((o) => ({ name: o.name || null, pose: seatPose(o) }))
    .filter((s) => s.pose);
}

// ── SEATS IN A ROOM THAT HAS NO MESH TO READ ────────────────────────────────
//
// Khari, 2026-08-04: "why did you put the listening room in the salon when we
// literally have a 'listening room' on the map?" — and then the reason it was
// not there: "I don't think we have the 3D equivalent of this room, the way we
// do in the salon."
//
// He is right, and the first version of this was wrong in a way worth naming:
// capacity was read from `userData.affordance`, which only a BUILT room carries.
// The Listening Room is a Marble SPLAT — a scan. A scan has no parts, no names
// and no affordances, so the feature was invisible in the room it is named
// after and worked in the salon, which is backwards.
//
// So a seat can also be PLACED — a `world_objects` row of kind 'seat', dropped
// by eye in ⚙ exactly the way residents are. That is already the convention for
// splat rooms (nobody can derive a couch's position from a point cloud; you go
// and stand next to it), and it makes seating authorable for any room at all,
// scanned or built.
//
// PLACED SEATS WIN over mesh affordances when both exist: a row means a person
// deliberately chose that spot, and a deliberate choice should not be outvoted
// by whatever the builder happened to tag.
export function seatsFromObjects(rows = []) {
  return rows
    .filter((o) => o && o.kind === 'seat' && Array.isArray(o.pos))
    // Ordered by slot then id, so the server's seat INDEX resolves to the same
    // chair on every client — the same contract the mesh path sorts by name for.
    .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0) || (a.object_id > b.object_id ? 1 : -1))
    .map((o) => ({
      name: o.ref || `seat-${o.object_id}`,
      pose: {
        // A placed seat is authored at FLOOR level (you stand where the seat is
        // and drop it), so the eye offset is added here rather than measured off
        // a cushion the way the mesh path does it.
        pos: [o.pos[0], (o.pos[1] || 0) + SEATED_EYE, o.pos[2]],
        yaw: o.rotY || 0,
        pitch: 0,
        reclined: false,
        label: 'Sit down',
        // HOW BIG THE PERSON IN THIS CHAIR IS. Per SEAT rather than per avatar,
        // because it is the furniture that decides scale: the same body has to
        // read right in an armchair and on a low couch, and roster.json's single
        // height cannot know which one it landed in. Null = use the roster's.
        bodyHeight: Number(o.data?.bodyHeight) || null,
      },
    }));
}

/** Whichever source this room actually has. Placed rows win; mesh is the floor. */
export function seatsFor(root, placedRows = []) {
  const placed = seatsFromObjects(placedRows);
  return placed.length ? placed : listSeats(root);
}
